import { PrimeIcons } from '@primevue/core/api';
import axios from 'axios';
import { useConfirm } from 'primevue/useconfirm';
import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { lowerCase } from '@rygen/rygen-ui/i18n';
import { DomainReference, Loadable, Optional } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import { useMileage, UseMileageResponse } from '@/composables/routeBuilder';
import {
  AddShipOrderToShipmentPayload,
  AddStopToShipmentPayload,
  AddToPlanningPoolPayload,
  CreateShipmentFromShipOrdersPayload,
  PlanningStatus,
  ProposedShipment,
  ProposedShipmentStop,
  RemoveFromPlanningPoolPayload,
  RemoveShipOrderFromShipmentPayload,
  RemoveStopFromShipmentPayload,
  RouteBuilderRequest,
  RoutePlanType,
  ScaffoldRoutePlanPayload,
  SelectRateGroupPayload,
  SplitShipOrderPayload,
  ValidShipOrder,
} from '@/model/routeBuilder';

import { ArrangeableShipment } from '@/components/multistop/ArrangeableShipmentStopList.vue';

export type RouteBuilderStep =
  'planning' | 'validation_error' | 'rating' | 'executing';

type PromiseSettledResult<T> =
  PromiseFulfilledResult<T> | PromiseRejectedResult;

const isFulfilled = <T>(
  result: PromiseSettledResult<T>,
): result is PromiseFulfilledResult<T> => result.status === 'fulfilled';

export interface UseRouteBuilderResponse {
  addPartialShipOrderToShipment: (
    shipOrder: ValidShipOrder,
    proposedShipment: ProposedShipment,
  ) => void;
  addProposedShipment: (
    stops: ProposedShipmentStop[],
    owner: Optional<DomainReference>,
  ) => void;
  addShipOrderToPlanningPool: (
    payload: AddToPlanningPoolPayload,
  ) => Promise<void>;
  addShipOrderToShipment: (payload: AddShipOrderToShipmentPayload) => void;
  addStopToShipment: (payload: AddStopToShipmentPayload) => void;
  /** `ShipOrder`s the user is adding to the planning pool */
  additionalShipOrders: Ref<Loadable<ValidShipOrder[]>>;
  clearProposedShipments: () => void;
  createShipmentFromShipOrders: (
    payload: CreateShipmentFromShipOrdersPayload,
  ) => void;
  currentStep: Ref<RouteBuilderStep>;
  findProposedShipment: (identifier: number) => {
    index: number;
    proposedShipment: ProposedShipment;
  };
  isOnPlanningStep: Ref<boolean>;
  loadRelatedShipOrdersForOrders: (
    loadable: Loadable<ValidShipOrder[]>,
    orderIds: number[],
  ) => Promise<void>;
  loadRelatedShipOrdersForShipments: (
    loadable: Loadable<ValidShipOrder[]>,
    shipmentIds: number[],
  ) => Promise<void>;
  mileages: UseMileageResponse['mileages'];
  planShipOrders: (payload: ScaffoldRoutePlanPayload) => void;
  planningPool: Ref<Loadable<ValidShipOrder[]>>;
  planningPoolShipOrders: Ref<ValidShipOrder[]>;
  /** `ProposedShipments` the user is building in the shipments section */
  proposedShipments: Ref<ProposedShipment[]>;
  rearrangeStops: (shipment: ArrangeableShipment) => void;
  removeShipOrderFromShipment: (
    payload: RemoveShipOrderFromShipmentPayload,
  ) => void;
  removeShipOrdersFromPlanningPool: (
    payload: RemoveFromPlanningPoolPayload,
  ) => void;
  removeShipment: (shipmentIdentifier: number) => void;
  removeStopFromShipment: (payload: RemoveStopFromShipmentPayload) => void;
  request: Ref<RouteBuilderRequest>;
  reset: () => void;
  selectRateGroup: (payload: SelectRateGroupPayload) => void;
  selectedRateGroup: Ref<Optional<SelectRateGroupPayload>>;
  splitShipOrder: (payload: SplitShipOrderPayload) => Promise<void>;
}

export const useRouteBuilder = (): UseRouteBuilderResponse => {
  const request: Ref<RouteBuilderRequest> = ref(new RouteBuilderRequest());

  /**
   * The proposed shipments the user is working on
   *
   * This is a temportary property that will eventually be replaced by
   * `request`. For now, we're only using `RouteBuilderRequest` to tie in to the
   * `ParentEntity` store to set the domain header correctly, but eventually
   * we will update everything to go through `request`.
   */
  const proposedShipments: Ref<ProposedShipment[]> = computed({
    get(): ProposedShipment[] {
      return request.value.proposedShipments;
    },
    set(newVal) {
      request.value.proposedShipments = newVal;
    },
  });

  const confirm = useConfirm();
  const { t } = useI18n();
  const { clearMileage, loadMileage, mileages } = useMileage(proposedShipments);
  const toast = useToast();

  const currentStep: Ref<RouteBuilderStep> = ref('planning');
  const selectedRateGroup: Ref<Optional<SelectRateGroupPayload>> = ref();

  const planningPool: Ref<Loadable<ValidShipOrder[]>> = ref({
    loading: false,
  });

  const additionalShipOrders: Ref<Loadable<ValidShipOrder[]>> = ref({
    loading: false,
  });

  const planningPoolShipOrders = computed({
    get() {
      return planningPool.value.data ?? [];
    },
    set(newVal: ValidShipOrder[]) {
      planningPool.value.data = newVal;
    },
  });

  const isOnPlanningStep = computed(() => {
    return (
      currentStep.value === 'planning' ||
      currentStep.value === 'validation_error'
    );
  });

  const loadRelatedShipOrdersForOrders = async (
    loadable: Loadable<ValidShipOrder[]>,
    orderIds: number[],
  ): Promise<void> => {
    try {
      loadable.error = undefined;
      loadable.loading = true;
      // TODO: Batch endpoint for loading related ship orders
      const promises = orderIds.map((orderId) =>
        api.order.findAllRelatedShipOrders(orderId, { onlyUnplanned: true }),
      );
      const resolvedPromises = await Promise.allSettled(promises);
      loadable.data = resolvedPromises
        .filter(isFulfilled)
        .map((result) => result.value)
        .flat();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error('Error loading ship orders', e);
      loadable.error = e;
      toast.addError({
        detail: t('errors.loadingParam', {
          param: lowerCase(t('consolidation.candidates', 2)),
        }),
        error: e,
      });
    } finally {
      loadable.loading = false;
    }
  };

  const loadRelatedShipOrdersForShipments = async (
    loadable: Loadable<ValidShipOrder[]>,
    shipmentIds: number[],
  ): Promise<void> => {
    try {
      loadable.error = undefined;
      loadable.loading = true;
      // TODO: Batch endpoint for loading related ship orders
      const promises = shipmentIds.map((shipmentId) =>
        api.shipment.findAllRelatedShipOrders(shipmentId),
      );
      const resolvedPromises = await Promise.allSettled(promises);
      loadable.data = resolvedPromises
        .filter(isFulfilled)
        .map((result) => result.value)
        .flat();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error('Error loading ship orders', e);
      loadable.error = e;
      toast.addError({
        detail: t('errors.loadingParam', {
          param: lowerCase(t('consolidation.candidates', 2)),
        }),
        error: e,
      });
    } finally {
      loadable.loading = false;
    }
  };

  const findProposedShipment = (
    identifier: number,
  ): { index: number; proposedShipment: ProposedShipment } => {
    const index = proposedShipments.value.findIndex(
      (s) => s.identifier === identifier,
    );
    const proposedShipment = proposedShipments.value[index];
    if (!proposedShipment) {
      throw new Error(`Error finding proposed shipment ${identifier}`);
    }

    return { index, proposedShipment };
  };

  const addProposedShipment = (
    stops: ProposedShipmentStop[],
    owner: Optional<DomainReference>,
  ) => {
    const identifiers = proposedShipments.value.map((s) => s.identifier);
    const nextIdentifier = Math.max(...identifiers, 0) + 1;
    const proposedShipment = new ProposedShipment({
      identifier: nextIdentifier,
      owner,
      stops,
    });

    proposedShipments.value.push(proposedShipment);

    loadMileage(proposedShipment.identifier);
  };

  const clearProposedShipments = () => {
    planningPoolShipOrders.value.forEach((so) => {
      so.origin.planningStatus = PlanningStatus.UNPLANNED;
      so.destination.planningStatus = PlanningStatus.UNPLANNED;
    });
    proposedShipments.value = [];
    mileages.value = {};
  };

  const addShipOrderToPlanningPool = async ({
    orderIds,
  }: AddToPlanningPoolPayload) => {
    await loadRelatedShipOrdersForOrders(additionalShipOrders.value, orderIds);
    const newShipOrders = additionalShipOrders.value.data ?? [];
    const currentShipOrders = planningPool.value.data ?? [];
    planningPool.value.data = [...currentShipOrders, ...newShipOrders];
  };

  const addShipOrderToShipment = ({
    shipOrder,
    shipmentIdentifier,
  }: AddShipOrderToShipmentPayload) => {
    const { proposedShipment } = findProposedShipment(shipmentIdentifier);

    const hasOrigin = proposedShipment.hasOriginStop(shipOrder);
    const hasDestination = proposedShipment.hasDestinationStop(shipOrder);
    if (hasOrigin && hasDestination) {
      onAddToShipmentConfirm(shipOrder, proposedShipment);
      return;
    }

    const stopType = hasOrigin ? t('orders.destination') : t('orders.origin');
    confirm.require({
      message: t('routeBuilder.leaveShipOrderUnresolved.message', {
        stopType,
        shipmentIdentifier: proposedShipment.identifier,
      }),
      icon: `${PrimeIcons.EXCLAMATION_TRIANGLE} text-warning`,
      group: 'rygen-confirm-dialog',
      header: t('routeBuilder.leaveShipOrderUnresolved.header'),
      accept: () => addPartialShipOrderToShipment(shipOrder, proposedShipment),
      acceptProps: {
        class: 'p-button-sm',
        label: t('routeBuilder.leaveShipOrderUnresolved.leaveUnresolved'),
        severity: 'warn',
      },
      reject: () => onAddToShipmentConfirm(shipOrder, proposedShipment),
      rejectProps: {
        class: 'p-button-sm p-button-text',
        label: t('common.addParam', { param: stopType }),
      },
    });
  };

  const addPartialShipOrderToShipment = (
    shipOrder: ValidShipOrder,
    proposedShipment: ProposedShipment,
  ) => {
    proposedShipment.addPartialShipOrder(shipOrder);
  };

  const onAddToShipmentConfirm = (
    shipOrder: ValidShipOrder,
    proposedShipment: ProposedShipment,
  ) => {
    proposedShipment.addShipOrder(shipOrder);

    loadMileage(proposedShipment.identifier);
  };

  const createShipmentFromShipOrders = (
    payload: CreateShipmentFromShipOrdersPayload,
  ) => {
    const shipOrderIds = payload.shipOrders.map((so) => so.id);
    planShipOrders({
      routePlanType: RoutePlanType.CONSOLIDATE,
      shipOrderIds,
    });
  };

  const removeShipOrderFromShipmentConfirm = (
    shipOrder: ValidShipOrder,
    shipmentIdentifier: number,
    skipMileage = false,
  ) => {
    const { proposedShipment } = findProposedShipment(shipmentIdentifier);

    const isOriginOnShipment = proposedShipment.hasOriginStop(shipOrder);
    const isDestinationOnShipment =
      proposedShipment.hasDestinationStop(shipOrder);
    proposedShipment.removeShipOrder(shipOrder);

    // Update removed shipOrder's planning status
    const planningPoolShipOrder = planningPoolShipOrders.value.find(
      (so) => so.id === shipOrder.id,
    );
    if (!planningPoolShipOrder) {
      throw new Error(
        `Could not find ship order ${shipOrder.id} in planning pool`,
      );
    }

    if (isOriginOnShipment) {
      planningPoolShipOrder.origin.planningStatus = PlanningStatus.UNPLANNED;
    }

    if (isDestinationOnShipment) {
      planningPoolShipOrder.destination.planningStatus =
        PlanningStatus.UNPLANNED;
    }

    if (!skipMileage) {
      loadMileage(shipmentIdentifier);
    }
  };

  const removeShipOrderFromShipment = ({
    shipOrder,
    shipmentIdentifiers,
    onConfirm,
  }: RemoveShipOrderFromShipmentPayload) => {
    confirm.require({
      message: t('routeBuilder.removeShipOrderConfirmation'),
      icon: `${PrimeIcons.EXCLAMATION_TRIANGLE} text-danger`,
      group: 'rygen-confirm-dialog',
      header: t('routeBuilder.leaveShipOrderUnresolved.header'),
      accept: () => {
        shipmentIdentifiers.forEach((identifier) =>
          removeShipOrderFromShipmentConfirm(shipOrder, identifier),
        );
        onConfirm?.();
      },
      acceptProps: {
        class: 'p-button-sm',
        label: t('common.removeParam', { param: t('shipOrders.shipOrders') }),
        icon: PrimeIcons.MINUS_CIRCLE,
        severity: 'danger',
      },
      rejectProps: {
        class: 'p-button-sm p-button-text',
        label: t('common.cancel'),
      },
    });
  };

  const removeStopFromShipment = ({
    stop,
    shipmentIdentifier,
    numShipOrdersOnStop,
  }: RemoveStopFromShipmentPayload) => {
    if (!numShipOrdersOnStop) {
      onRemoveStopFromShipmentConfirm({ stop, shipmentIdentifier });
      return;
    }

    confirm.require({
      group: 'rygen-confirm-dialog',
      message: t('routeBuilder.removeStopConfirmation', {
        count: numShipOrdersOnStop,
      }),
      header: t('common.confirmRemove'),
      accept: () =>
        onRemoveStopFromShipmentConfirm({ stop, shipmentIdentifier }),
      acceptProps: {
        class: 'p-button-sm',
        label: t('common.removeParam', {
          param: t('common.paramNum', {
            param: t('shipments.stops'),
            num: stop.sequenceNumber,
          }),
        }),
        icon: PrimeIcons.MINUS_CIRCLE,
        severity: 'danger',
      },
      rejectProps: {
        class: 'p-button-sm p-button-text',
        label: t('common.cancel'),
      },
    });
  };

  const onRemoveStopFromShipmentConfirm = ({
    stop,
    shipmentIdentifier,
  }: Pick<RemoveStopFromShipmentPayload, 'stop' | 'shipmentIdentifier'>) => {
    const { proposedShipment } = findProposedShipment(shipmentIdentifier);

    // hold onto shipping point ids on this stop so we can update their status
    // if they're removed
    const shipOrderShippingPointIds = stop.shipOrderShippingPointIds;

    const removedStop = proposedShipment.removeStop(stop.sequenceNumber);

    if (removedStop) {
      planningPoolShipOrders.value
        .filter(
          (so) =>
            shipOrderShippingPointIds.includes(so.origin.id) ||
            shipOrderShippingPointIds.includes(so.destination.id),
        )
        .forEach((so) => {
          const shippingPoint = shipOrderShippingPointIds.includes(so.origin.id)
            ? so.origin
            : so.destination;
          shippingPoint.planningStatus = PlanningStatus.UNPLANNED;
        });

      removedStop.isPlaceholderStop
        ? clearMileage(shipmentIdentifier)
        : loadMileage(shipmentIdentifier);
    }
  };

  const addStopToShipment = (payload: AddStopToShipmentPayload) => {
    const { proposedShipment } = findProposedShipment(
      payload.shipmentIdentifier,
    );

    if (
      payload.stopSelector === 'origin' ||
      payload.stopSelector === 'destination'
    ) {
      const stop = proposedShipment[payload.stopSelector];
      if (stop) {
        stop.location = payload.location;
      }
    } else if (payload.sequenceNumber) {
      const stop = proposedShipment.stops.find(
        (s) => s.sequenceNumber === payload.sequenceNumber,
      );
      if (stop) {
        stop.location = payload.location;
      }
    } else {
      proposedShipment.addStop(payload);
    }

    loadMileage(proposedShipment.identifier);
  };

  const rearrangeStops = (shipment: ArrangeableShipment) => {
    const isProposedShipment = shipment instanceof ProposedShipment;
    if (!isProposedShipment) {
      console.error('`shipment` should be a ProposedShipment', shipment);
      throw new Error('`shipment` should be a ProposedShipment');
    }

    const { proposedShipment } = findProposedShipment(shipment.identifier);
    proposedShipment.stops = shipment.stops;
    loadMileage(proposedShipment.identifier);
  };

  const removeShipment = (shipmentIdentifier: number) => {
    const { proposedShipment } = findProposedShipment(shipmentIdentifier);

    proposedShipment.shipOrders
      .map((so) => {
        const planningPoolShipOrder = planningPoolShipOrders.value.find(
          (ppso) => ppso.id === so.id,
        );
        if (!planningPoolShipOrder) {
          throw new Error(
            `Could not find ship order ${so.id} in planning pool`,
          );
        }

        return planningPoolShipOrder;
      })
      .forEach((so) => {
        removeShipOrderFromShipmentConfirm(so, shipmentIdentifier, true);
      });

    proposedShipments.value = proposedShipments.value.filter(
      (s) => s.identifier !== shipmentIdentifier,
    );
    mileages.value[shipmentIdentifier] = {
      response: { loading: false },
    };
  };

  const removeShipOrdersFromPlanningPool = (
    payload: RemoveFromPlanningPoolPayload,
  ) => {
    const shipOrderIds = payload.shipOrders.map((so) => so.id).filter(notNull);
    planningPoolShipOrders.value = planningPoolShipOrders.value.filter(
      (so) => so.id && !shipOrderIds.includes(so.id),
    );
  };

  const splitShipOrder = async (payload: SplitShipOrderPayload) => {
    try {
      planningPool.value.loading = true;

      const response = await api.shipOrder.splitBeta(payload);
      const existingShipOrderIndex = planningPoolShipOrders.value.findIndex(
        (so) => so.id === response.originalShipOrder.id,
      );
      const shipOrdersToInsert = [
        response.originalShipOrder,
        ...response.newShipOrders,
      ];
      planningPoolShipOrders.value.splice(
        existingShipOrderIndex,
        1,
        ...shipOrdersToInsert,
      );
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error('Error splitting ship order', e);
      toast.addError({
        detail: t('shipOrders.shipOrderSplit.errors.splitting'),
        error: e,
      });
    } finally {
      planningPool.value.loading = false;
    }
  };

  const planShipOrders = async (payload: ScaffoldRoutePlanPayload) => {
    if (payload.routePlanType !== RoutePlanType.CONSOLIDATE) {
      toast.addWarning({ detail: 'Not Implemented Yet' });
      return;
    }

    const numExistingShipments = proposedShipments.value.length;
    let newShipmentIdentifier = numExistingShipments + 1;

    const shipOrders = planningPoolShipOrders.value.filter((so) =>
      payload.shipOrderIds.includes(so.id),
    );
    const [firstShipOrder, ...otherShipOrders] = shipOrders;

    const newShipment = ProposedShipment.fromShipOrder(-1, firstShipOrder);
    otherShipOrders.forEach((so) => newShipment.addShipOrder(so));

    newShipment.identifier = newShipmentIdentifier++;
    proposedShipments.value.push(newShipment);
    loadMileage(newShipment.identifier);
  };

  const reset = () => {
    planningPool.value = { loading: false };
    additionalShipOrders.value = { loading: false };
    proposedShipments.value = [];
    currentStep.value = 'planning';
    selectedRateGroup.value = undefined;
  };

  const selectRateGroup = (payload: SelectRateGroupPayload) => {
    selectedRateGroup.value = payload;
    currentStep.value = 'executing';
  };

  return {
    addPartialShipOrderToShipment,
    addProposedShipment,
    addShipOrderToPlanningPool,
    addShipOrderToShipment,
    addStopToShipment,
    additionalShipOrders,
    clearProposedShipments,
    createShipmentFromShipOrders,
    currentStep,
    findProposedShipment,
    isOnPlanningStep,
    loadRelatedShipOrdersForOrders,
    loadRelatedShipOrdersForShipments,
    mileages,
    planShipOrders,
    planningPool,
    planningPoolShipOrders,
    proposedShipments,
    rearrangeStops,
    removeShipOrderFromShipment,
    removeShipOrdersFromPlanningPool,
    removeShipment,
    removeStopFromShipment,
    request,
    reset,
    selectRateGroup,
    selectedRateGroup,
    splitShipOrder,
  };
};
